import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, brl } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { TrendingUp, TrendingDown, Minus, RefreshCw, Radar as RadarIcon, ChevronRight } from "lucide-react";

const PURPLE = "#5B2CA0";
const DEEP = "#2B1E4A";
const RISK = {
  muito_baixo: { c: "#2E7D46", bg: "#EAF6EE", label: "Muito baixo" },
  baixo: { c: "#3F6B45", bg: "#EFF5EF", label: "Baixo" },
  medio: { c: "#B7891B", bg: "#FBF6E7", label: "Moderado" },
  elevado: { c: "#C2560A", bg: "#FCEEE2", label: "Elevado" },
  alto: { c: "#B4564F", bg: "#FBEDED", label: "Alto" },
};
const GROUPS = [
  { key: "INCREASE", title: "Oportunidades de aumento", c: "#2E7D46" },
  { key: "MAINTAIN", title: "Manter limite", c: "#5C5A57" },
  { key: "REVIEW", title: "Revisão recomendada", c: "#C2560A" },
  { key: "REDUCE", title: "Possível redução", c: "#B4564F" },
  { key: "NEW", title: "Histórico insuficiente", c: "#8A7788" },
];

function Trend({ t }) {
  t = Number(t || 0);
  if (t > 0) return <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-[#2E7D46]"><TrendingUp size={12} />+{t}</span>;
  if (t < 0) return <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-[#B4564F]"><TrendingDown size={12} />{t}</span>;
  return <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-[#8A7788]"><Minus size={12} /></span>;
}

const WOULD = {
  WOULD_APPROVE: { t: "Seria aprovado", c: "#2E7D46", bg: "#EAF6EE" },
  WOULD_MAINTAIN: { t: "Manter", c: "#5C5A57", bg: "#F2EFEA" },
  WOULD_REVIEW: { t: "Revisão manual", c: "#C2560A", bg: "#FCEEE2" },
  WOULD_REDUCE: { t: "Possível redução", c: "#B4564F", bg: "#FBEDED" },
  BLOCKED: { t: "Bloqueado", c: "#B4564F", bg: "#FBEDED" },
};
function WouldBadge({ wd }) {
  const w = WOULD[wd];
  if (!w) return null;
  return <span className="hidden rounded-full px-2 py-0.5 text-[10px] font-bold md:inline" style={{ color: w.c, background: w.bg }} data-testid={`radar-would-${wd}`}>Dry-run: {w.t}</span>;
}

function Row({ r, isAdmin }) {
  const risk = RISK[r.global_risk] || RISK.medio;
  const mainFactor = (r.factors && r.factors[0]?.detail) || (r.blocked_reasons || [])[0] || "—";
  const to = isAdmin ? `/admin/clientes/${r.client_id}` : `/revenda/clientes/${r.client_id}`;
  return (
    <Link to={to} className="flex items-center gap-3 rounded-xl border border-[#EBE5DF] bg-white p-3 hover:border-[#C9B7EC]" data-testid={`radar-row-${r.client_id}`}>
      <div className="grid h-10 w-12 place-items-center rounded-lg font-display text-lg font-bold" style={{ color: DEEP, background: "#F3EFFA" }}>{r.global_score}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-bold" style={{ color: DEEP }}>{r.client_name || r.client_id}</span>
          <Trend t={r.trend} />
        </div>
        <div className="truncate text-[11px] text-[#8A7788]">{mainFactor}</div>
      </div>
      <div className="hidden text-right sm:block">
        <div className="text-[11px] text-[#8A7788]">{brl(r.current_limit)} → <span className="font-bold" style={{ color: PURPLE }}>{brl(r.recommended_limit)}</span></div>
        {Number(r.increase) !== 0 && <div className="text-[11px] font-bold" style={{ color: r.increase > 0 ? "#2E7D46" : "#B4564F" }}>{r.increase > 0 ? "+" : ""}{brl(r.increase)}</div>}
      </div>
      {r.would_decision && <WouldBadge wd={r.would_decision} />}
      <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ color: risk.c, background: risk.bg }}>{risk.label}</span>
      <ChevronRight size={16} className="text-[#C9B7EC]" />
    </Link>
  );
}

export default function CreditRadar() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ unit: "", risk: "", scoreMin: "", scoreMax: "", onlyIncrease: false, onlyReduce: false, onlyNew: false, onlyOverdue: false });

  const load = async () => {
    setErr("");
    try { const { data } = await api.get("/credit/radar"); setRows(data || []); }
    catch (e) { setErr(e.response?.data?.detail || "Não foi possível carregar o Radar."); }
  };
  useEffect(() => { load(); }, []);

  const recompute = async () => {
    if (!isAdmin) return;
    setBusy(true);
    try { const { data } = await api.post("/credit/recompute"); await load(); }
    catch (e) { /* silencioso */ }
    finally { setBusy(false); }
  };

  const filtered = useMemo(() => {
    let list = rows || [];
    if (f.risk) list = list.filter((r) => r.global_risk === f.risk);
    if (f.scoreMin) list = list.filter((r) => r.global_score >= Number(f.scoreMin));
    if (f.scoreMax) list = list.filter((r) => r.global_score <= Number(f.scoreMax));
    if (f.onlyIncrease) list = list.filter((r) => r.category === "INCREASE");
    if (f.onlyReduce) list = list.filter((r) => r.category === "REDUCE");
    if (f.onlyNew) list = list.filter((r) => r.is_new_client);
    if (f.onlyOverdue) list = list.filter((r) => (r.blocked_reasons || []).includes("parcela_vencida"));
    if (f.unit === "CRIS") list = list.filter((r) => !((r.unit_scores || {}).CRIS_LINGERIE || {}).insufficient_history);
    if (f.unit === "ELETROS") list = list.filter((r) => !((r.unit_scores || {}).ELETROS || {}).insufficient_history);
    return list;
  }, [rows, f]);

  const grouped = useMemo(() => {
    const g = { INCREASE: [], MAINTAIN: [], REVIEW: [], REDUCE: [], NEW: [] };
    for (const r of filtered) {
      if (r.is_new_client) g.NEW.push(r);
      else (g[r.category] || g.MAINTAIN).push(r);
    }
    return g;
  }, [filtered]);

  return (
    <div data-testid="credit-radar-page">
      <div className="topbar">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">CRIS CREDIT</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1 flex items-center gap-2"><RadarIcon size={28} color={PURPLE} /> Radar de Crédito</h1>
          <p className="text-sm text-[#5C5A57] mt-2 max-w-2xl">Oportunidades e riscos de limite calculados pelo motor. Decisões são sempre manuais.</p>
        </div>
        {isAdmin && <button onClick={recompute} disabled={busy} className="brand" data-testid="radar-recompute-btn">{busy ? "Recalculando..." : "Recalcular carteira"}</button>}
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="radar-filters">
        <select value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} className="rounded-lg border border-[#EBE5DF] px-3 py-2 text-sm" data-testid="radar-filter-unit">
          <option value="">Todas as unidades</option><option value="CRIS">Cris Lingerie</option><option value="ELETROS">Eletros</option>
        </select>
        <select value={f.risk} onChange={(e) => setF({ ...f, risk: e.target.value })} className="rounded-lg border border-[#EBE5DF] px-3 py-2 text-sm" data-testid="radar-filter-risk">
          <option value="">Todos os riscos</option>
          {Object.entries(RISK).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <input type="number" placeholder="Score mín" value={f.scoreMin} onChange={(e) => setF({ ...f, scoreMin: e.target.value })} className="w-24 rounded-lg border border-[#EBE5DF] px-3 py-2 text-sm" data-testid="radar-filter-scoremin" />
        <input type="number" placeholder="Score máx" value={f.scoreMax} onChange={(e) => setF({ ...f, scoreMax: e.target.value })} className="w-24 rounded-lg border border-[#EBE5DF] px-3 py-2 text-sm" data-testid="radar-filter-scoremax" />
        {[["onlyIncrease", "Oportunidade"], ["onlyReduce", "Redução"], ["onlyNew", "Novos"], ["onlyOverdue", "Parcela vencida"]].map(([k, lbl]) => (
          <button key={k} onClick={() => setF({ ...f, [k]: !f[k] })} className={`rounded-full px-3 py-1.5 text-xs font-bold ${f[k] ? "text-white" : "text-[#5C5A57]"}`} style={{ background: f[k] ? PURPLE : "#F2EFEA" }} data-testid={`radar-filter-${k}`}>{lbl}</button>
        ))}
      </div>

      {err ? (
        <div className="rounded-xl border-2 border-[#E0D6F0] bg-white p-6" data-testid="radar-error">
          <p className="text-sm text-[#8B5E5E]">{err}</p>
          <button onClick={load} className="brand mt-3" data-testid="radar-retry-btn">Tentar novamente</button>
        </div>
      ) : !rows ? (
        <div className="text-sm text-[#8A7788]" data-testid="radar-loading">Carregando...</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-[#EBE5DF] bg-white p-10 text-center text-sm text-[#8A7788]" data-testid="radar-empty">
          Nenhum cliente analisado ainda. {isAdmin && "Use \"Recalcular carteira\" para gerar as análises."}
        </div>
      ) : (
        <div className="space-y-6">
          {GROUPS.map((g) => grouped[g.key].length > 0 && (
            <section key={g.key} data-testid={`radar-group-${g.key}`}>
              <div className="mb-2 flex items-center gap-2">
                <span className="h-3 w-3 rounded-full" style={{ background: g.c }} />
                <h2 className="text-sm font-bold uppercase tracking-widest" style={{ color: g.c }}>{g.title}</h2>
                <span className="rounded-full bg-[#F2EFEA] px-2 py-0.5 text-[11px] font-bold text-[#5C5A57]">{grouped[g.key].length}</span>
              </div>
              <div className="space-y-2">{grouped[g.key].map((r) => <Row key={r.client_id} r={r} isAdmin={isAdmin} />)}</div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
